import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  approvalRate,
  buildReturnCustomerRows,
  buildReturnProductRows,
  buildReturnReasonRows,
  ControlLogicName,
  ControlRow,
  CustomerAccessRow,
  ReturnRecord,
  SiteRow,
  sumReturnTotals,
  UncontrolledProduct,
} from "../../../utils/opsReports";
import { byMonth, ChartDatum, MonthDatum, topTally } from "../../../utils/codReportCharts";
import {
  ChartCard,
  ColumnChart,
  fmt,
  fmtCount,
  HBarChart,
  Segment,
  SERIES,
  StackedBar,
  TableSpec,
} from "../codSalesRep/CodReportCharts";

const gridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
  gap: 2,
} as const;

const rankTable = (name: string, value: string, data: ChartDatum[], format: (v: number) => string): TableSpec => ({
  columns: [name, value],
  rows: data.map((d) => [d.sub && d.sub !== d.label ? `${d.label} (${d.sub})` : d.label, format(d.value)]),
});

const monthTable = (month: string, value: string, data: MonthDatum[], format: (v: number) => string): TableSpec => ({
  columns: [month, value],
  rows: data.map((d) => [d.label, format(d.value)]),
});

const segmentTable = (value: string, segments: Segment[], format: (v: number) => string): TableSpec => ({
  columns: ["", value],
  rows: segments.map((s) => [s.label, format(s.value)]),
});

const isEmpty = (data: { value: number }[]) => data.every((d) => d.value <= 0);

/* ══ Supervisor: returns ════════════════════════════════════════════════════════ */

export const SupervisorChartsSection: React.FC<{
  records: ReturnRecord[];
  productName: (itemId: string) => string;
  reasonLabel: (reason: string) => string;
}> = ({ records, productName, reasonLabel }) => {
  const { t } = useTranslation();
  const other = t("codReports.other");
  const month = t("codReports.month");

  const customerRows = buildReturnCustomerRows(records);
  const totals = sumReturnTotals(customerRows);

  const statusSegments: Segment[] = [
    { key: "approved", label: t("opsReports.approved"), value: totals.approved, color: SERIES.blue },
    { key: "pending", label: t("opsReports.pending"), value: totals.pending, color: SERIES.orange },
    { key: "rejected", label: t("opsReports.rejected"), value: totals.rejected, color: SERIES.aqua },
    { key: "cancelled", label: t("opsReports.cancelled"), value: totals.cancelled, color: SERIES.yellow },
  ];
  const valueSegments: Segment[] = [
    { key: "approved", label: t("opsReports.approvedValue"), value: totals.approvedValue, color: SERIES.blue },
    { key: "pending", label: t("opsReports.pendingValue"), value: totals.pendingValue, color: SERIES.orange },
    { key: "rejected", label: t("opsReports.rejectedValue"), value: totals.rejectedValue, color: SERIES.aqua },
  ];

  const requestsMonthly = byMonth(records, (r) => r.requestDate, () => 1);
  const valueMonthly = byMonth(records, (r) => r.requestDate, (r) => r.amount);

  const topCustomers = topTally(
    customerRows.map((row) => ({
      key: row.key,
      label: row.customerName || row.customerCode || "—",
      sub: row.customerCode,
      value: row.requestedValue,
    })),
    8,
    other
  );
  const topProducts = topTally(
    buildReturnProductRows(records, productName).map((row) => ({
      key: row.key,
      label: row.name,
      sub: row.itemId,
      value: row.quantity,
    })),
    8,
    other
  );
  const reasons = topTally(
    buildReturnReasonRows(records).map((row) => ({ key: row.key, label: reasonLabel(row.reason), value: row.quantity })),
    6,
    other
  );

  return (
    <Box sx={gridSx}>
      <ChartCard
        title={t("opsReports.chartReturnStatus")}
        subtitle={`${t("opsReports.approvalRate")}: ${(approvalRate(totals) * 100).toFixed(0)}%`}
        empty={isEmpty(statusSegments)}
        table={segmentTable(t("opsReports.requests"), statusSegments, fmtCount)}
      >
        <StackedBar segments={statusSegments} ariaLabel={t("opsReports.chartReturnStatus")} format={fmtCount} />
      </ChartCard>
      <ChartCard
        title={t("opsReports.chartReturnValue")}
        subtitle={t("opsReports.chartReturnValueHint")}
        empty={isEmpty(valueSegments)}
        table={segmentTable(t("opsReports.value"), valueSegments, fmt)}
      >
        <StackedBar segments={valueSegments} ariaLabel={t("opsReports.chartReturnValue")} />
      </ChartCard>
      <ChartCard
        title={t("opsReports.chartRequestsByMonth")}
        empty={requestsMonthly.length === 0}
        table={monthTable(month, t("opsReports.requests"), requestsMonthly, fmtCount)}
      >
        <ColumnChart data={requestsMonthly} ariaLabel={t("opsReports.chartRequestsByMonth")} format={fmtCount} />
      </ChartCard>
      <ChartCard
        title={t("opsReports.chartValueByMonth")}
        empty={valueMonthly.length === 0}
        table={monthTable(month, t("opsReports.requestedValue"), valueMonthly, fmt)}
      >
        <ColumnChart data={valueMonthly} ariaLabel={t("opsReports.chartValueByMonth")} />
      </ChartCard>
      <ChartCard
        title={t("opsReports.chartTopCustomers")}
        subtitle={t("opsReports.requestedValue")}
        empty={topCustomers.length === 0}
        table={rankTable(t("opsReports.customer"), t("opsReports.requestedValue"), topCustomers, fmt)}
      >
        <HBarChart data={topCustomers} ariaLabel={t("opsReports.chartTopCustomers")} />
      </ChartCard>
      <ChartCard
        title={t("opsReports.chartTopProducts")}
        subtitle={t("opsReports.quantity")}
        empty={topProducts.length === 0}
        table={rankTable(t("opsReports.product"), t("opsReports.quantity"), topProducts, fmtCount)}
      >
        <HBarChart data={topProducts} ariaLabel={t("opsReports.chartTopProducts")} format={fmtCount} />
      </ChartCard>
      <ChartCard
        title={t("opsReports.chartReasons")}
        subtitle={t("opsReports.quantity")}
        empty={reasons.length === 0}
        table={rankTable(t("opsReports.reason"), t("opsReports.quantity"), reasons, fmtCount)}
      >
        <HBarChart data={reasons} ariaLabel={t("opsReports.chartReasons")} format={fmtCount} />
      </ChartCard>
    </Box>
  );
};

/* ══ WHM: item control ════════════════════════════════════════════════════════ */

export const WhmChartsSection: React.FC<{
  controlRows: ControlRow[];
  siteRows: SiteRow[];
  customerRows: CustomerAccessRow[];
  uncontrolledRows: UncontrolledProduct[];
  logicLabel: (logic: ControlLogicName) => string;
}> = ({ controlRows, siteRows, customerRows, uncontrolledRows, logicLabel }) => {
  const { t } = useTranslation();
  const other = t("codReports.other");
  const count = (logic: ControlLogicName) => controlRows.filter((row) => row.controlLogic === logic).length;

  const typeSegments: Segment[] = [
    { key: "None", label: logicLabel("None"), value: count("None"), color: SERIES.blue },
    { key: "Consumption", label: logicLabel("Consumption"), value: count("Consumption"), color: SERIES.orange },
    { key: "Customer", label: logicLabel("Customer"), value: count("Customer"), color: SERIES.aqua },
  ];

  const releasedBySite = topTally(
    siteRows.map((row) => ({ key: row.key, label: row.site, value: row.quantityReleased })),
    8,
    other
  );

  const productTally = new Map<string, { key: string; label: string; sub?: string; value: number }>();
  controlRows.forEach((row) => {
    const key = row.itemCode.toLowerCase() || row.id;
    const entry = productTally.get(key) ?? { key, label: row.productName, sub: row.itemCode, value: 0 };
    entry.value += row.quantityReleased;
    productTally.set(key, entry);
  });
  const topProducts = topTally(Array.from(productTally.values()), 8, other);

  const topAccess = topTally(
    customerRows.map((row) => ({ key: row.key, label: row.customerName, value: row.products.length })),
    8,
    other
  );

  const uncontrolledTally = new Map<string, { key: string; label: string; value: number }>();
  uncontrolledRows.forEach((row) => {
    const label = row.site || "—";
    const entry = uncontrolledTally.get(label) ?? { key: label, label, value: 0 };
    entry.value += 1;
    uncontrolledTally.set(label, entry);
  });
  const uncontrolledBySite = topTally(Array.from(uncontrolledTally.values()), 8, other);

  const releasesMonthly = byMonth(controlRows, (row) => row.releaseDate, () => 1);

  return (
    <Box sx={gridSx}>
      <ChartCard
        title={t("opsReports.chartControlsByType")}
        empty={isEmpty(typeSegments)}
        table={segmentTable(t("opsReports.controls"), typeSegments, fmtCount)}
      >
        <StackedBar segments={typeSegments} ariaLabel={t("opsReports.chartControlsByType")} format={fmtCount} />
      </ChartCard>
      <ChartCard
        title={t("opsReports.chartReleasedBySite")}
        subtitle={t("opsReports.qtyReleased")}
        empty={releasedBySite.length === 0}
        table={rankTable(t("opsReports.site"), t("opsReports.qtyReleased"), releasedBySite, fmtCount)}
      >
        <HBarChart data={releasedBySite} ariaLabel={t("opsReports.chartReleasedBySite")} format={fmtCount} />
      </ChartCard>
      <ChartCard
        title={t("opsReports.chartTopReleased")}
        subtitle={t("opsReports.qtyReleased")}
        empty={topProducts.length === 0}
        table={rankTable(t("opsReports.product"), t("opsReports.qtyReleased"), topProducts, fmtCount)}
      >
        <HBarChart data={topProducts} ariaLabel={t("opsReports.chartTopReleased")} format={fmtCount} />
      </ChartCard>
      <ChartCard
        title={t("opsReports.chartTopAccess")}
        subtitle={t("opsReports.chartTopAccessHint")}
        empty={topAccess.length === 0}
        table={rankTable(t("opsReports.customer"), t("opsReports.productsCount"), topAccess, fmtCount)}
      >
        <HBarChart data={topAccess} ariaLabel={t("opsReports.chartTopAccess")} format={fmtCount} />
      </ChartCard>
      <ChartCard
        title={t("opsReports.chartUncontrolledBySite")}
        subtitle={t("opsReports.chartUncontrolledHint")}
        empty={uncontrolledBySite.length === 0}
        table={rankTable(t("opsReports.site"), t("opsReports.productsCount"), uncontrolledBySite, fmtCount)}
      >
        <HBarChart data={uncontrolledBySite} ariaLabel={t("opsReports.chartUncontrolledBySite")} format={fmtCount} />
      </ChartCard>
      <ChartCard
        title={t("opsReports.chartReleasesByMonth")}
        subtitle={t("opsReports.controls")}
        empty={releasesMonthly.length === 0}
        table={monthTable(t("codReports.month"), t("opsReports.controls"), releasesMonthly, fmtCount)}
      >
        <ColumnChart data={releasesMonthly} ariaLabel={t("opsReports.chartReleasesByMonth")} format={fmtCount} />
      </ChartCard>
    </Box>
  );
};
